import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  BarChart2,
  Filter,
  Plus,
  RefreshCw,
  Save,
  Search,
  Edit,
  Copy,
  X,
  Check,
  Users,
  Info,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { Block, StrategyTemplate } from '../../types';
import { listTemplates, copyTemplate, deleteTemplate } from '../../services/templateService';
import { useAuth } from '../../contexts/AuthContext';
import SaveStrategyDialog from './SaveStrategyDialog';
import ValidationStatusChip from '../Validation/ValidationStatusChip';
import { useValidationEngineGate } from '../../config/useValidationEngineGate';
import { getStrategyValidationStatuses } from '../../services/validationService';
import type { ValidationStatus } from '../../types/validation';

// ── Template Gallery QA helpers (X.1+24 stabilization) ───────────────
//
// Surface the v-N marker + deferred-features inventory directly on
// the template card so users see the "this is a v1; here's what
// isn't covered yet" context BEFORE they click Load. Both signals are
// derived from the existing template fields (no schema change needed):
//
//   - `parseVersionLabel` reads the trailing "(v1)" / "(v2)" / etc.
//     from the template name. Returns null when there's no version
//     suffix — older templates render unchanged.
//   - `parseDeferredFeatures` walks the description for the phrase
//     "Deferred features" and extracts the comma- or sentence-
//     separated list that follows. Returns [] when the section
//     isn't present.
//   - `extractPropFirmProfile` looks at the strategy_config block's
//     `data.propFirm` field (populated by X.1+22-A-aware templates)
//     and returns a compact { provider, preset } summary for the
//     card chip.
function parseVersionLabel(name: string): string | null {
  const m = name.match(/\((v\d+(?:\.\d+)*)\)\s*$/i);
  return m ? m[1].toLowerCase() : null;
}

function parseDeferredFeatures(description: string | undefined | null): string[] {
  if (!description) return [];
  // Look for the explicit "Deferred features" marker the FTMO v1
  // template uses. The list that follows ends at the next sentence
  // boundary or the next "—" / "(" delimiter.
  const idx = description.toLowerCase().indexOf('deferred features');
  if (idx === -1) return [];
  // Take everything from the marker onward, trim to the next
  // sentence-ending period (excluding periods inside parens).
  let tail = description.slice(idx);
  // Drop the leading "Deferred features (…):" header — keep the
  // list portion.
  tail = tail.replace(/^deferred features[^:]*:\s*/i, '');
  // Cut at the next sentence boundary (a period followed by a
  // space + uppercase, OR the end of the string).
  const sentenceEnd = tail.search(/\.\s+[A-Z]/);
  if (sentenceEnd !== -1) tail = tail.slice(0, sentenceEnd);
  return tail
    .split(/,\s*/)
    .map(s => s.replace(/\.\s*$/, '').trim())
    .filter(s => s.length > 0 && s.length < 200);
}

function extractPropFirmProfile(blocks: Block[] | undefined | null): { provider: string; preset?: string } | null {
  if (!Array.isArray(blocks)) return null;
  const cfg = blocks.find(b => b.type === 'strategy_config');
  if (!cfg) return null;
  const pf = (cfg.data as { propFirm?: { provider?: string }; propFirmPreset?: string } | undefined)?.propFirm;
  if (!pf || !pf.provider) return null;
  const preset = (cfg.data as { propFirmPreset?: string } | undefined)?.propFirmPreset;
  return { provider: String(pf.provider), preset: preset ? String(preset) : undefined };
}

function countCustomBlocks(blocks: Block[] | undefined | null): number {
  if (!Array.isArray(blocks)) return 0;
  return blocks.filter(b => b.type === 'custom').length;
}

interface TemplateBrowserProps {
  onApplyTemplate: (blocks: Block[], templateId?: string) => void;
}

type TemplateOwnerFilter = 'all' | 'mine' | 'others';

const TemplateBrowser: React.FC<TemplateBrowserProps> = ({
  onApplyTemplate,
}) => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState<StrategyTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    difficulty: '',
    platform: '',
    assetClass: '',
  });
  const [ownerFilter, setOwnerFilter] = useState<TemplateOwnerFilter>('all');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [selectedTemplate, setSelectedTemplate] = useState<StrategyTemplate | null>(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [copyingTemplate, setCopyingTemplate] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  // X.1+24 stabilization — Template Gallery QA. Opens a small modal
  // showing the full description + deferred-features list + prop-firm
  // profile preview + custom-block inventory + a confirm "Load" button.
  // Lets users review what they're about to load before they click.
  const [detailsTemplate, setDetailsTemplate] = useState<StrategyTemplate | null>(null);
  const [metadata, setMetadata] = useState({
    name: '',
    description: '',
    isPublic: false,
  });

  useEffect(() => {
    fetchTemplates();
  }, [filters, user, ownerFilter]);

  // Validation Engine (flag-gated): fetch latest Reality Check status per
  // listed strategy so the card can show a status chip. No-op when off.
  const validationGate = useValidationEngineGate();
  const [validationStatuses, setValidationStatuses] = useState<Record<string, ValidationStatus>>({});
  useEffect(() => {
    if (!validationGate.enabled || templates.length === 0) return;
    let cancelled = false;
    void getStrategyValidationStatuses(templates.map((t) => t.id)).then((m) => {
      if (!cancelled) setValidationStatuses(m);
    });
    return () => { cancelled = true; };
  }, [validationGate.enabled, templates]);

  useEffect(() => {
    if (copySuccess) {
      const timer = setTimeout(() => {
        setCopySuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [copySuccess]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching templates with user:', user?.id);
      const data = await listTemplates(filters);
      console.log('Fetched templates:', data);
      setTemplates(data);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to fetch templates'
      );
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUseTemplate = async (template: StrategyTemplate) => {
    try {
      onApplyTemplate(template.blocks, template.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to use template');
    }
  };

  const handleSaveTemplate = (template: StrategyTemplate) => {
    setSelectedTemplate(template);
    setShowSaveDialog(true);
  };

  const handleCopyTemplate = (template: StrategyTemplate) => {
    setSelectedTemplate(template);
    setMetadata({
      name: `Copy of ${template.name}`,
      description: template.description || '',
      isPublic: false,
    });
    setShowCopyDialog(true);
  };

  const handleSaveComplete = (strategyId: string) => {
    setShowSaveDialog(false);
    setSelectedTemplate(null);
    console.log('Strategy saved with ID:', strategyId);
    fetchTemplates(); // Refresh the list
  };

  // S28 (2026-06-04) — Delete an owned template. Confirms first
  // (deletes are destructive + irreversible) then calls the existing
  // templateService.deleteTemplate (Supabase DELETE on
  // strategy_templates — RLS already restricts to author rows).
  // On success: refresh the list. On error: surface inline so the
  // user knows nothing was deleted.
  const handleDeleteTemplate = async (template: StrategyTemplate) => {
    const label = template.name || 'this template';
    if (!window.confirm(
      `Delete "${label}"? This cannot be undone, and anyone who copied it ` +
      `keeps their copy.`,
    )) return;
    try {
      setError(null);
      await deleteTemplate(template.id);
      fetchTemplates();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete template');
    }
  };

  const handleCopyComplete = async () => {
    try {
      setCopyingTemplate(true);
      setError(null);

      if (!selectedTemplate) {
        throw new Error('No template selected');
      }

      const templateId = await copyTemplate(selectedTemplate.id, {
        name: metadata.name || `Copy of ${selectedTemplate.name}`,
        description: metadata.description || selectedTemplate.description,
        isPublic: metadata.isPublic,
      });

      setShowCopyDialog(false);
      setSelectedTemplate(null);
      setCopySuccess(true);
      console.log('Template copied with ID:', templateId);
      fetchTemplates(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to copy template');
    } finally {
      setCopyingTemplate(false);
    }
  };

  const filteredTemplates = templates
    .filter((template) => {
      // Apply owner filter
      if (ownerFilter === 'mine' && user) {
        return template.author?.id === user.id;
      } else if (ownerFilter === 'others' && user) {
        return template.author?.id !== user.id;
      }
      return true;
    })
    .filter(
      (template) =>
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        template.tags?.some((tag: string) =>
          tag.toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">
          Strategy Templates
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={() => setView('grid')}
            className={`p-2 rounded-md ${
              view === 'grid'
                ? 'bg-blue-100 text-blue-800'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <BarChart2 className="w-5 h-5" />
          </button>
          <button
            onClick={() => setView('list')}
            className={`p-2 rounded-md ${
              view === 'list'
                ? 'bg-blue-100 text-blue-800'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Filter className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:space-x-4">
          <div className="relative flex-1 mb-4 md:mb-0">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search templates..."
              className="w-full pl-10 pr-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
          </div>

          <div className="flex flex-wrap gap-2">
            {/* Owner filter */}
            <select
              value={ownerFilter}
              onChange={(e) =>
                setOwnerFilter(e.target.value as TemplateOwnerFilter)
              }
              className="rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Templates</option>
              <option value="mine">My Templates</option>
              <option value="others">Community Templates</option>
            </select>

            <select
              value={filters.category}
              onChange={(e) =>
                setFilters({ ...filters, category: e.target.value })
              }
              className="rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Categories</option>
              <option value="trend">Trend Following</option>
              <option value="momentum">Momentum</option>
              <option value="breakout">Breakout</option>
              <option value="mean-reversion">Mean Reversion</option>
            </select>

            <select
              value={filters.difficulty}
              onChange={(e) =>
                setFilters({ ...filters, difficulty: e.target.value })
              }
              className="rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Difficulties</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>

            <select
              value={filters.platform}
              onChange={(e) =>
                setFilters({ ...filters, platform: e.target.value })
              }
              className="rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Platforms</option>
              <option value="mt4">MetaTrader 4</option>
              <option value="mt5">MetaTrader 5</option>
              <option value="tradingview">TradingView</option>
            </select>

            <select
              value={filters.assetClass}
              onChange={(e) =>
                setFilters({ ...filters, assetClass: e.target.value })
              }
              className="rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Asset Classes</option>
              <option value="forex">Forex</option>
              <option value="stocks">Stocks</option>
              <option value="crypto">Crypto</option>
              <option value="commodities">Commodities</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700">
          <AlertTriangle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      <button
        onClick={fetchTemplates}
        className="mb-6 flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
      >
        <RefreshCw className="w-4 h-4 mr-2" />
        Refresh Templates
      </button>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="text-center py-12">
          <Plus className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-800 mb-2">
            No templates found
          </h3>
          <p className="text-gray-600">
            Try adjusting your search or filters to find more templates
          </p>
        </div>
      ) : view === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => (
            <div
              key={template.id}
              className="bg-white rounded-lg shadow-md border border-gray-200 hover:border-blue-300 transition-colors"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <h3 className="text-lg font-semibold text-gray-800">
                        {template.name.replace(/\s*\((v\d+(?:\.\d+)*)\)\s*$/i, '').trim()}
                      </h3>
                      {/* X.1+24 stabilization — v-N marker chip parsed
                          from the template name (e.g. "(v1)" suffix).
                          Surfaces "this is an explicit version" so
                          users know to expect deferred-features. */}
                      {(() => {
                        const v = parseVersionLabel(template.name);
                        return v ? (
                          <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-indigo-100 text-indigo-800 border border-indigo-200">
                            {v}
                          </span>
                        ) : null;
                      })()}
                      {/* Prop-firm profile chip — present only when the
                          template's strategy_config carries a
                          propFirm.provider field (X.1+22-A profile). */}
                      {(() => {
                        const pf = extractPropFirmProfile(template.blocks);
                        if (!pf) return null;
                        const providerLabel = pf.provider === 'ftmo'
                          ? 'FTMO'
                          : pf.provider === 'goat_funded_trader'
                            ? 'Goat'
                            : pf.provider;
                        return (
                          <span
                            className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-100 text-emerald-800 border border-emerald-200 inline-flex items-center gap-0.5"
                            title={`Prop-firm profile: ${providerLabel}${pf.preset ? ` · ${pf.preset}` : ''}`}
                          >
                            <ShieldCheck className="w-2.5 h-2.5" />
                            {providerLabel}
                          </span>
                        );
                      })()}
                      {/* Validation Engine status chip (flag-gated; empty until
                          a verdict snapshot exists for this strategy). */}
                      {validationStatuses[template.id] && (
                        <ValidationStatusChip status={validationStatuses[template.id]} />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {template.description}
                    </p>
                  </div>
                  <div className="flex items-center">
                    {user &&
                      template.author &&
                      user.id === template.author.id && (
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mr-2">
                          <Users className="w-3 h-3 inline mr-1" />
                          Mine
                        </span>
                      )}
                    <Save className="w-5 h-5 text-yellow-400" />
                    <span className="ml-1 text-sm font-medium text-gray-700">
                      {template.stats?.downloads || 0}
                    </span>
                  </div>
                </div>

                {/* X.1+24 stabilization — "N deferred features" badge.
                    Parses the template description for the explicit
                    "Deferred features" section the FTMO v1 template
                    uses. Empty list → no badge (older templates render
                    unchanged). Clicking opens the Details modal. */}
                {(() => {
                  const deferred = parseDeferredFeatures(template.description);
                  const customCount = countCustomBlocks(template.blocks);
                  if (deferred.length === 0 && customCount === 0) return null;
                  return (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {deferred.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setDetailsTemplate(template)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-900 border border-amber-200 hover:bg-amber-100"
                          title="Open details to see what this template doesn't yet cover"
                        >
                          <AlertTriangle className="w-3 h-3" />
                          {deferred.length} deferred feature{deferred.length === 1 ? '' : 's'}
                        </button>
                      )}
                      {customCount > 0 && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-violet-50 text-violet-900 border border-violet-200"
                          title="This template uses CUSTOM blocks for non-canonical logic"
                        >
                          {customCount} custom block{customCount === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                  );
                })()}

                <div className="flex flex-wrap gap-2 mb-4">
                  {template.tags &&
                    template.tags.map((tag: string, index: number) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800"
                      >
                        {tag}
                      </span>
                    ))}
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  <div className="flex items-center text-gray-600">
                    <BarChart2 className="w-4 h-4 mr-1.5" />
                    <span>{template.difficulty}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Filter className="w-4 h-4 mr-1.5" />
                    <span>{template.platform}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <ArrowUp className="w-4 h-4 mr-1.5" />
                    <span>{template.stats?.rating?.toFixed(1) || 'N/A'}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <ArrowDown className="w-4 h-4 mr-1.5" />
                    <span>{template.stats?.downloads || 0}</span>
                  </div>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => handleUseTemplate(template)}
                    className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Load Template
                  </button>
                  <button
                    onClick={() => setDetailsTemplate(template)}
                    className="flex items-center justify-center px-3 py-2 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors"
                    title="View full details + deferred features"
                  >
                    <Info className="w-4 h-4" />
                  </button>
                  {user && template.author && user.id === template.author.id ? (
                    <>
                      <button
                        onClick={() => handleSaveTemplate(template)}
                        className="flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                        title="Edit Template"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {/* S28 — Delete (owner-only). Adjacent to Edit
                          so the owner controls cluster together. */}
                      <button
                        onClick={() => handleDeleteTemplate(template)}
                        className="flex items-center justify-center px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                        title="Delete this template"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleCopyTemplate(template)}
                      className="flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                      title="Create a Copy"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredTemplates.map((template) => (
            <div
              key={template.id}
              className="bg-white rounded-lg shadow-md border border-gray-200 hover:border-blue-300 transition-colors p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-800">
                      {template.name}
                    </h3>
                    <div className="flex items-center ml-4">
                      {user &&
                        template.author &&
                        user.id === template.author.id && (
                          <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mr-2">
                            <Users className="w-3 h-3 inline mr-1" />
                            Mine
                          </span>
                        )}
                      <Save className="w-5 h-5 text-yellow-400" />
                      <span className="ml-1 text-sm font-medium text-gray-700">
                        {template.stats?.downloads || 0}
                      </span>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mb-3">
                    {template.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-3">
                    {template.tags &&
                      template.tags.map((tag: string, index: number) => (
                        <span
                          key={index}
                          className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800"
                        >
                          {tag}
                        </span>
                      ))}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center text-gray-600">
                      <BarChart2 className="w-4 h-4 mr-1.5" />
                      <span>{template.difficulty}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Filter className="w-4 h-4 mr-1.5" />
                      <span>{template.platform}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <ArrowUp className="w-4 h-4 mr-1.5" />
                      <span>{template.stats?.rating?.toFixed(1) || 'N/A'}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <ArrowDown className="w-4 h-4 mr-1.5" />
                      <span>{template.stats?.downloads || 0}</span>
                    </div>
                  </div>
                </div>

                <div className="ml-6 flex space-x-2">
                  <button
                    onClick={() => handleUseTemplate(template)}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Use Template
                  </button>
                  {user && template.author && user.id === template.author.id ? (
                    <>
                      <button
                        onClick={() => handleSaveTemplate(template)}
                        className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                        title="Edit Template"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {/* S28 — Delete (owner-only), list view. */}
                      <button
                        onClick={() => handleDeleteTemplate(template)}
                        className="flex items-center px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                        title="Delete this template"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleCopyTemplate(template)}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                      title="Create a Copy"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showSaveDialog && selectedTemplate && (
        <SaveStrategyDialog
          blocks={selectedTemplate.blocks}
          templateId={selectedTemplate.id}
          onClose={() => setShowSaveDialog(false)}
          onSaved={handleSaveComplete}
        />
      )}

      {/* X.1+24 stabilization — Template Details modal.
          Shows full description, deferred-features inventory,
          prop-firm profile preview, custom-block count, tags, and
          a "Load Template" CTA so users can review before loading.
          Triggered by the new Info button on each card OR by the
          "N deferred features" amber badge. */}
      {detailsTemplate && (() => {
        const t = detailsTemplate;
        const version = parseVersionLabel(t.name);
        const deferred = parseDeferredFeatures(t.description);
        const pf = extractPropFirmProfile(t.blocks);
        const customCount = countCustomBlocks(t.blocks);
        const totalBlocks = Array.isArray(t.blocks) ? t.blocks.length : 0;
        const nameClean = t.name.replace(/\s*\((v\d+(?:\.\d+)*)\)\s*$/i, '').trim();
        const close = () => setDetailsTemplate(null);
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={close}
            onKeyDown={(e) => { if (e.key === 'Escape') close(); }}
          >
            <div
              className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-1.5">
                    <h3 className="text-base font-semibold text-slate-900">{nameClean}</h3>
                    {version && (
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-indigo-100 text-indigo-800 border border-indigo-200">
                        {version}
                      </span>
                    )}
                    {pf && (
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-100 text-emerald-800 border border-emerald-200 inline-flex items-center gap-0.5">
                        <ShieldCheck className="w-2.5 h-2.5" />
                        {pf.provider === 'ftmo' ? 'FTMO' : pf.provider === 'goat_funded_trader' ? 'Goat' : pf.provider}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {totalBlocks} block{totalBlocks === 1 ? '' : 's'}
                    {customCount > 0 && ` · ${customCount} custom`}
                    {t.difficulty && ` · ${t.difficulty}`}
                    {t.platform && ` · ${t.platform}`}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={close}
                  className="text-slate-500 hover:text-slate-700"
                  title="Close (Esc)"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 text-sm">
                <div>
                  <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">Description</h4>
                  <p className="text-slate-800 leading-relaxed whitespace-pre-line">{t.description}</p>
                </div>

                {deferred.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                    <h4 className="text-xs font-semibold text-amber-900 uppercase tracking-wide mb-1.5 inline-flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Deferred features ({deferred.length})
                    </h4>
                    <ul className="list-disc pl-5 space-y-0.5 text-[12px] text-amber-900">
                      {deferred.map((f, i) => (<li key={i}>{f}</li>))}
                    </ul>
                    <p className="mt-2 text-[11px] text-amber-800">
                      These were part of the source strategy but are not yet covered by this template.
                      Loading the template still works; the deferred logic is simply absent. Add custom
                      blocks or wait for a later template version to cover them.
                    </p>
                  </div>
                )}

                {pf && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
                    <h4 className="text-xs font-semibold text-emerald-900 uppercase tracking-wide mb-1.5 inline-flex items-center gap-1">
                      <ShieldCheck className="w-3.5 h-3.5" />
                      Prop-firm profile attached
                    </h4>
                    <div className="text-[12px] text-emerald-900 space-y-0.5">
                      <div>
                        <strong>Provider:</strong> {pf.provider === 'ftmo' ? 'FTMO' : pf.provider === 'goat_funded_trader' ? 'Goat Funded Trader' : pf.provider}
                      </div>
                      {pf.preset && <div><strong>Preset:</strong> <code className="bg-emerald-100 px-1 rounded">{pf.preset}</code></div>}
                    </div>
                    <p className="mt-2 text-[11px] text-emerald-800">
                      The X.1+22-A rule engine enforces max-daily-loss, max-drawdown, intraday-only, and
                      one-trade-per-day from this profile at evaluation time — the block graph doesn&apos;t
                      need to re-implement them.
                    </p>
                  </div>
                )}

                {Array.isArray(t.tags) && t.tags.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Tags</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {t.tags.map((tag, i) => (
                        <span key={i} className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-blue-100 text-blue-800">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="px-5 py-3 border-t flex items-center justify-between gap-2 bg-slate-50">
                <button
                  type="button"
                  onClick={close}
                  className="px-3 py-1.5 text-sm rounded-md bg-white border border-slate-300 text-slate-700 hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => { void handleUseTemplate(t); close(); }}
                  className="px-4 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-1.5"
                >
                  <Check className="w-4 h-4" />
                  Load Template
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showCopyDialog && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-medium text-gray-800">
                Create a Copy
              </h3>
              <button
                onClick={() => setShowCopyDialog(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4">
              <p className="text-gray-600 mb-4">
                Create your own copy of this template that you can modify and
                customize.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Name
                  </label>
                  <input
                    type="text"
                    value={metadata.name}
                    onChange={(e) =>
                      setMetadata({ ...metadata, name: e.target.value })
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={`Copy of ${selectedTemplate.name}`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={metadata.description}
                    onChange={(e) =>
                      setMetadata({ ...metadata, description: e.target.value })
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder={selectedTemplate.description}
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isPublic"
                    checked={metadata.isPublic}
                    onChange={(e) =>
                      setMetadata({ ...metadata, isPublic: e.target.checked })
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label
                    htmlFor="isPublic"
                    className="ml-2 text-sm text-gray-700"
                  >
                    Make this template public
                  </label>
                </div>
              </div>

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700 text-sm">
                  <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowCopyDialog(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCopyComplete}
                  disabled={copyingTemplate}
                  className={`flex items-center px-4 py-2 rounded-md text-white font-medium transition-colors ${
                    copyingTemplate
                      ? 'bg-blue-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {copyingTemplate ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                      Copying...
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-1.5" />
                      Create a Copy
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {copySuccess && (
        <div className="fixed bottom-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded shadow-md">
          <div className="flex items-center">
            <Check className="h-6 w-6 text-green-500 mr-4" />
            <div>
              <p className="font-bold">Success</p>
              <p className="text-sm">Template copied successfully</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateBrowser;
